/**
 * AvatarHealthBadge.tsx
 *
 * Always-on small status pip rendered on top of the avatar canvas.
 *
 *   - Dim green dot when everything is nominal (`overall === 'ok'`).
 *   - Amber dot with a count when degraded (`overall === 'warn'`).
 *   - Red dot with a count when failing (`overall === 'fail'`).
 *   - Click to expand a small popover listing each issue with severity,
 *     short label, optional detail, and a relative timestamp.
 *
 * Why visible by default? Three reasons:
 *
 *   1. The avatar is the user's primary trust surface on DM, AgentLink
 *      and Visual IVR. When something goes wrong silently — STUN-only
 *      fallback, audio access denied, WebGL context lost — they have no
 *      way to know "the experience is degraded but recoverable" vs
 *      "the experience is broken and I should reload". The pip closes
 *      that gap.
 *
 *   2. Operators triaging a customer report ("the avatar didn't talk
 *      back") can ask "is the dot green, amber, or red?" instead of
 *      asking the user to open devtools.
 *
 *   3. The pip is *small* (10px) and sits in the top-right of the
 *      canvas. It's noticeable to a deliberate look but doesn't compete
 *      with the avatar's face for attention. The amber/red states are
 *      intentionally more prominent because that's when the user needs
 *      to know.
 *
 * Opt-out: pass `hidden` to suppress the badge entirely. Use this when
 * embedding the avatar in a context where any extra UI is unwelcome
 * (e.g. a tiny chat-thumb in a contextual chat panel).
 *
 * Sizing: the badge size scales with `size` prop (default 10px). The
 * popover is fixed-width to keep typography consistent across surfaces.
 *
 * Positioning: defaults to top-right (`position='top-right'`). Override
 * if the avatar canvas has an existing UI element competing for the
 * corner.
 */

import { useEffect, useRef, useState } from 'react'

import type { AvatarHealthIssue, AvatarHealthSeverity, AvatarHealthState } from '../../hooks/useAvatarHealth'

export interface AvatarHealthBadgeProps {
  /**
   * Aggregate health state from `useAvatarHealth()`. The badge is a
   * pure render of this state — all signal aggregation happens in the
   * hook so the badge is trivial to test and reuse.
   */
  state: AvatarHealthState
  /**
   * Hide the badge entirely (e.g. for a tiny chat-thumb context where
   * any extra UI is unwelcome). Defaults to false (badge visible).
   */
  hidden?: boolean
  /**
   * Pip diameter in pixels. Defaults to 10. Larger values make the
   * badge more prominent — useful on full-screen avatar surfaces
   * (Visual IVR, DM full screen).
   */
  size?: number
  /**
   * Corner of the parent canvas. Defaults to 'top-right'. Use
   * 'bottom-right' on surfaces where the top-right is taken (e.g.
   * AgentLink's handoff page has a "close" button up there).
   */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  /**
   * Extra inline styles to apply to the badge wrapper. Use for fine
   * positioning adjustments on surfaces with existing chrome.
   */
  style?: React.CSSProperties
}

const PIP_COLORS: Record<AvatarHealthSeverity, string> = {
  ok: 'rgba(74, 222, 128, 0.55)',
  warn: 'rgba(251, 191, 36, 0.95)',
  fail: 'rgba(239, 68, 68, 0.95)',
}

const PIP_RING_COLORS: Record<AvatarHealthSeverity, string> = {
  ok: 'rgba(34, 197, 94, 0.25)',
  warn: 'rgba(245, 158, 11, 0.45)',
  fail: 'rgba(220, 38, 38, 0.5)',
}

function relativeTime(ms: number): string {
  if (!ms || ms <= 0) return ''
  const ageS = Math.max(0, Math.floor((Date.now() - ms) / 1000))
  if (ageS < 60) return `${ageS}s ago`
  const ageM = Math.floor(ageS / 60)
  if (ageM < 60) return `${ageM}m ago`
  const ageH = Math.floor(ageM / 60)
  return `${ageH}h ago`
}

function severityLabel(severity: AvatarHealthSeverity, count: number): string {
  if (severity === 'ok') return 'All systems healthy'
  if (severity === 'warn') return `${count} degraded`
  return `${count} failing`
}

function positionStyle(position: NonNullable<AvatarHealthBadgeProps['position']>): React.CSSProperties {
  switch (position) {
    case 'top-left':
      return { top: 8, left: 8 }
    case 'bottom-right':
      return { bottom: 8, right: 8 }
    case 'bottom-left':
      return { bottom: 8, left: 8 }
    case 'top-right':
    default:
      return { top: 8, right: 8 }
  }
}

function popoverPositionStyle(position: NonNullable<AvatarHealthBadgeProps['position']>): React.CSSProperties {
  switch (position) {
    case 'top-left':
      return { top: '100%', left: 0, marginTop: 8 }
    case 'bottom-right':
      return { bottom: '100%', right: 0, marginBottom: 8 }
    case 'bottom-left':
      return { bottom: '100%', left: 0, marginBottom: 8 }
    case 'top-right':
    default:
      return { top: '100%', right: 0, marginTop: 8 }
  }
}

export function AvatarHealthBadge({
  state,
  hidden = false,
  size = 10,
  position = 'top-right',
  style,
}: AvatarHealthBadgeProps): JSX.Element | null {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Click-outside to close. The badge is small so this is the primary
  // dismiss path; we also wire Escape for keyboard users below.
  useEffect(() => {
    if (!open) return
    const handlePointer = (evt: PointerEvent) => {
      if (!wrapperRef.current) return
      if (wrapperRef.current.contains(evt.target as Node)) return
      setOpen(false)
    }
    const handleKey = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', handlePointer)
    window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('pointerdown', handlePointer)
      window.removeEventListener('keydown', handleKey)
    }
  }, [open])

  if (hidden) return null

  const overall = state.overall
  const issues = state.issues
  const issueCount = issues.length
  const pipColor = PIP_COLORS[overall]
  const ringColor = PIP_RING_COLORS[overall]

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'absolute',
        zIndex: 50,
        pointerEvents: 'auto',
        ...positionStyle(position),
        ...style,
      }}
    >
      <button
        type="button"
        aria-label={severityLabel(overall, issueCount)}
        title={severityLabel(overall, issueCount)}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          borderRadius: '50%',
          padding: 0,
          border: `1px solid ${ringColor}`,
          background: pipColor,
          boxShadow: overall === 'fail'
            ? `0 0 0 4px ${ringColor}, 0 0 8px ${pipColor}`
            : overall === 'warn'
              ? `0 0 0 3px ${ringColor}`
              : `0 0 0 2px ${ringColor}`,
          cursor: 'pointer',
          transition: 'transform 120ms ease, box-shadow 120ms ease',
          transform: open ? 'scale(1.2)' : 'scale(1)',
        }}
      />

      {open && (
        <div
          role="dialog"
          aria-label="Avatar health details"
          style={{
            position: 'absolute',
            ...popoverPositionStyle(position),
            width: 280,
            maxWidth: 'calc(100vw - 32px)',
            background: 'rgba(17, 24, 39, 0.95)',
            color: 'rgba(243, 244, 246, 0.95)',
            borderRadius: 8,
            padding: '10px 12px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 600,
              fontSize: 12,
              marginBottom: issueCount > 0 ? 8 : 0,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: pipColor,
              }}
            />
            <span>{severityLabel(overall, issueCount)}</span>
          </div>

          {issueCount === 0 ? (
            <div style={{ fontSize: 11, color: 'rgba(209, 213, 219, 0.85)' }}>
              Audio, video, voice, and 3D rendering are all healthy.
            </div>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {issues.map((issue) => (
                <AvatarHealthIssueRow key={issue.id} issue={issue} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function AvatarHealthIssueRow({ issue }: { issue: AvatarHealthIssue }): JSX.Element {
  const dot = issue.severity === 'fail' ? PIP_COLORS.fail : PIP_COLORS.warn
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        background: 'rgba(255, 255, 255, 0.04)',
        borderRadius: 6,
        padding: '6px 8px',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: dot,
          marginTop: 6,
          flex: '0 0 auto',
        }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'rgba(243, 244, 246, 0.95)',
          }}
        >
          {issue.label}
        </div>
        {issue.detail && (
          <div
            style={{
              fontSize: 11,
              color: 'rgba(209, 213, 219, 0.75)',
              marginTop: 2,
              wordBreak: 'break-word',
            }}
          >
            {issue.detail}
          </div>
        )}
        <div
          style={{
            fontSize: 10,
            color: 'rgba(156, 163, 175, 0.7)',
            marginTop: 2,
          }}
        >
          {relativeTime(issue.firstSeenAt)}
        </div>
      </div>
    </li>
  )
}

export default AvatarHealthBadge
